import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, Mail, ArrowRight, AlertCircle, RefreshCw, CheckCircle2, Lock, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useAuthStore } from '@/store/authStore';
import apiClient from '@/lib/api-client';
import axios from 'axios';

export function VerifySubscriptionPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { tenant, updateTenantSubscription, user, fetchUser, isAuthenticated } = useAuthStore();

    // Trigger fetchUser if authenticated but user state not hydrated yet
    useEffect(() => {
        if (isAuthenticated && !user) {
            fetchUser();
        }
    }, [isAuthenticated, user, fetchUser]);

    // Robust multi-source tenant ID resolver that rejects literal "undefined" or "null" strings
    const resolvedTenantId = useMemo(() => {
        const queryVal = searchParams.get('tenantId');
        if (queryVal && queryVal !== 'undefined' && queryVal !== 'null' && queryVal.trim()) {
            return queryVal.trim();
        }
        if (tenant?.id && tenant.id !== 'undefined' && tenant.id !== 'null') {
            return tenant.id;
        }
        if (user?.tenantId && user.tenantId !== 'undefined' && user.tenantId !== 'null') {
            return user.tenantId;
        }
        const localSaved = localStorage.getItem('tenantId');
        if (localSaved && localSaved !== 'undefined' && localSaved !== 'null') {
            return localSaved;
        }
        try {
            const token = localStorage.getItem('accessToken');
            if (token) {
                const payload = JSON.parse(atob(token.split('.')[1]));
                if (payload?.tenantId && payload.tenantId !== 'undefined' && payload.tenantId !== 'null') {
                    return payload.tenantId;
                }
            }
        } catch {}

        return '';
    }, [searchParams, tenant, user]);

    // Robust email resolver
    const resolvedEmail = useMemo(() => {
        const queryEmail = searchParams.get('email');
        if (queryEmail && queryEmail !== 'undefined' && queryEmail !== 'null' && queryEmail.trim()) {
            return queryEmail.trim();
        }
        if (user?.email) return user.email;
        try {
            const token = localStorage.getItem('accessToken');
            if (token) {
                const payload = JSON.parse(atob(token.split('.')[1]));
                if (payload?.email) return payload.email;
            }
        } catch {}
        return '';
    }, [searchParams, user]);

    const tenantId = resolvedTenantId;
    const emailParam = resolvedEmail;

    // Cache resolved tenantId in localStorage for session resilience
    useEffect(() => {
        if (tenantId && tenantId !== 'undefined') {
            localStorage.setItem('tenantId', tenantId);
        }
    }, [tenantId]);

    // Clean up malformed "?tenantId=undefined" from URL if detected
    useEffect(() => {
        const rawParam = searchParams.get('tenantId');
        if (rawParam === 'undefined' || rawParam === 'null') {
            const newParams = new URLSearchParams(searchParams);
            if (tenantId) {
                newParams.set('tenantId', tenantId);
            } else {
                newParams.delete('tenantId');
            }
            setSearchParams(newParams, { replace: true });
        }
    }, [searchParams, tenantId, setSearchParams]);

    const [pin, setPin] = useState<string[]>(['', '', '', '', '', '']);
    const [isLoading, setIsLoading] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [cooldown, setCooldown] = useState(0);

    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    useEffect(() => {
        // Auto focus first input on mount
        inputRefs.current[0]?.focus();
    }, []);

    // Cooldown timer for resend
    useEffect(() => {
        if (cooldown <= 0) return;
        const timer = setInterval(() => setCooldown((c) => c - 1), 1000);
        return () => clearInterval(timer);
    }, [cooldown]);

    const handleChange = (index: number, value: string) => {
        const cleanVal = value.replace(/\D/g, '').slice(-1);
        const newPin = [...pin];
        newPin[index] = cleanVal;
        setPin(newPin);
        setError('');

        if (cleanVal && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !pin[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        if (!pastedData) return;

        const newPin = [...pin];
        for (let i = 0; i < 6; i++) {
            newPin[i] = pastedData[i] || '';
        }
        setPin(newPin);

        const nextIndex = Math.min(pastedData.length, 5);
        inputRefs.current[nextIndex]?.focus();
    };

    const fullPin = pin.join('');
    const isComplete = fullPin.length === 6;

    const handleVerify = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (!isComplete) return;

        try {
            setError('');
            setIsLoading(true);

            if (!tenantId || tenantId === 'undefined') {
                throw new Error('Tenant identification is missing. Please log in or register your business.');
            }

            const { data } = await apiClient.post(`/tenants/${tenantId}/verify-subscription`, {
                pin: fullPin,
            });

            updateTenantSubscription(true);
            try {
                await fetchUser();
            } catch {}

            setSuccessMsg(data?.data?.message || 'Subscription successfully verified!');

            // Short celebration before navigating
            setTimeout(() => {
                navigate('/dashboard', { replace: true });
            }, 800);
        } catch (err: any) {
            if (axios.isAxiosError(err)) {
                setError(
                    err.response?.data?.error?.message ||
                    err.response?.data?.message ||
                    'Invalid 6-digit activation code. Please try again.'
                );
            } else {
                setError(err.message || 'Verification failed. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleResend = async () => {
        if (cooldown > 0 || isResending) return;
        if (!tenantId || tenantId === 'undefined') {
            setError('Tenant identification is missing. Please sign in again.');
            return;
        }

        try {
            setIsResending(true);
            setError('');
            setSuccessMsg('');

            await apiClient.post(`/tenants/${tenantId}/admin-generate-pin`);
            setSuccessMsg('A new activation code has been generated and dispatched to your email.');
            setCooldown(60);
        } catch {
            setError('Please contact your platform administrator or support team to resend your activation code.');
        } finally {
            setIsResending(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50 relative overflow-hidden">
            {/* Background glowing gradients */}
            <div className="absolute top-1/4 -left-20 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />

            <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="w-full max-w-md"
            >
                <div className="bg-white rounded-3xl border border-slate-200/80 p-8 shadow-xl shadow-emerald-500/5 text-slate-800 relative">
                    {/* Top Accent Gradient Bar */}
                    <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600 rounded-t-3xl" />

                    {/* Header Icon */}
                    <div className="flex flex-col items-center text-center mb-6 pt-2">
                        <motion.div
                            whileHover={{ scale: 1.05 }}
                            className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-emerald-500 to-teal-600 p-0.5 shadow-lg shadow-emerald-500/20 mb-4 flex items-center justify-center"
                        >
                            <div className="w-full h-full bg-white rounded-[14px] flex items-center justify-center">
                                <ShieldCheck size={32} className="text-emerald-600" />
                            </div>
                        </motion.div>
                        <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">
                            Activate Subscription
                        </h1>
                        <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">
                            Enter the 6-digit activation code sent to your email to verify your subscription and unlock full platform access.
                        </p>
                        {emailParam && (
                            <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-xs text-emerald-800 font-medium">
                                <Mail size={12} className="text-emerald-600" />
                                <span>{emailParam}</span>
                            </div>
                        )}
                    </div>

                    {/* Feedback Messages */}
                    <AnimatePresence mode="wait">
                        {error && (
                            <motion.div
                                initial={{ opacity: 0, y: -8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -8 }}
                                className="mb-5 p-3.5 bg-rose-50 border border-rose-200 rounded-xl flex items-start gap-2.5 text-rose-700 text-xs"
                            >
                                <AlertCircle size={16} className="text-rose-500 flex-shrink-0 mt-0.5" />
                                <span>{error}</span>
                            </motion.div>
                        )}
                        {successMsg && (
                            <motion.div
                                initial={{ opacity: 0, y: -8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -8 }}
                                className="mb-5 p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl flex items-start gap-2.5 text-emerald-800 text-xs"
                            >
                                <CheckCircle2 size={16} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                                <span>{successMsg}</span>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Missing Tenant ID Fallback Banner */}
                    {!tenantId && (
                        <div className="mb-5 p-3.5 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-between text-xs text-amber-800">
                            <span className="flex items-center gap-1.5">
                                <AlertCircle size={15} className="text-amber-600 flex-shrink-0" />
                                <span>No business organization identified.</span>
                            </span>
                            <Link to="/login" className="font-bold underline hover:text-amber-900 ml-2">
                                Sign In →
                            </Link>
                        </div>
                    )}

                    {/* PIN Boxes Form */}
                    <form onSubmit={handleVerify}>
                        <div className="flex justify-between items-center gap-2 mb-6" onPaste={handlePaste}>
                            {pin.map((digit, idx) => (
                                <input
                                    key={idx}
                                    ref={(el) => {
                                        inputRefs.current[idx] = el;
                                    }}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={1}
                                    value={digit}
                                    onChange={(e) => handleChange(idx, e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(idx, e)}
                                    className="w-12 h-14 sm:w-14 sm:h-16 text-center text-2xl font-bold rounded-2xl bg-slate-50 border-2 border-slate-200 text-slate-900 focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-emerald-500/10 transition-all font-mono shadow-sm"
                                />
                            ))}
                        </div>

                        <Button
                            type="submit"
                            size="lg"
                            fullWidth
                            disabled={!isComplete || isLoading || !tenantId}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3.5 rounded-xl shadow-lg shadow-emerald-600/20 transition-all"
                            rightIcon={<ArrowRight size={18} />}
                        >
                            {isLoading ? 'Verifying Activation Code...' : 'Activate & Enter Dashboard'}
                        </Button>
                    </form>

                    {/* Don't have a code? View packages banner */}
                    <div className="mt-5 p-4 rounded-2xl bg-gradient-to-r from-emerald-50 via-teal-50/60 to-emerald-50 border border-emerald-100/80 text-center">
                        <p className="text-xs font-semibold text-emerald-900 mb-1 flex items-center justify-center gap-1.5">
                            <CreditCard size={14} className="text-emerald-600" />
                            Don't have an activation code or need to subscribe?
                        </p>
                        <p className="text-[11px] text-slate-600 mb-2.5">
                            Browse single & multi-business plans, custom feature pricing, or local installation packages.
                        </p>
                        <Link
                            to={`/subscribe?tenantId=${tenantId}&email=${encodeURIComponent(emailParam)}`}
                            className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700 hover:text-emerald-800 bg-white px-3 py-1.5 rounded-lg border border-emerald-200 shadow-sm transition-all hover:shadow"
                        >
                            View Subscription Packages & Pay →
                        </Link>
                    </div>

                    {/* Resend Action */}
                    <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                        <span>Didn't get the email?</span>
                        <button
                            type="button"
                            onClick={handleResend}
                            disabled={cooldown > 0 || isResending}
                            className="inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-700 font-medium disabled:text-slate-400 disabled:cursor-not-allowed transition-colors"
                        >
                            <RefreshCw size={12} className={isResending ? 'animate-spin' : ''} />
                            {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Request New Code'}
                        </button>
                    </div>

                    {/* Dev Mode / Master PIN Helper */}
                    <div className="mt-4 p-2.5 rounded-xl bg-slate-50 border border-slate-200/80 text-[11px] text-slate-500 flex items-center justify-between">
                        <span className="flex items-center gap-1.5">
                            <Lock size={12} className="text-amber-500" />
                            <span>Platform Admin Master PIN:</span>
                        </span>
                        <code className="px-2 py-0.5 rounded bg-white border border-slate-200 text-emerald-700 font-mono font-bold">999999</code>
                    </div>

                    <div className="mt-4 text-center">
                        <Link
                            to="/login"
                            className="text-xs text-slate-500 hover:text-slate-800 font-medium transition-colors"
                        >
                            ← Back to Login
                        </Link>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}
